import React, { useState, useEffect, useCallback } from 'react';
import { 
  Loader2, 
  Plus, 
  Pencil, 
  Trash2, 
  ImagePlus,
  GripVertical,
  Eye,
  EyeOff,
  Star,
  ChevronUp,
  ChevronDown,
  Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { categoriesAPI, productsAPI } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMenu = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [importing, setImporting] = useState(false);
  
  // Category Modal
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', order: 0, active: true, available_days: [] });
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Product Modal
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    category_id: '',
    base_price: 0,
    image_url: '',
    variations: [],
    extras: [],
    complement_groups: [],
    preference_options: null,
    available: true,
    featured: false,
    rodizio_incluido: 'nao',
    rodizio_only: false,
    vendus_tax_id: 'INT'
  });
  const [newVariation, setNewVariation] = useState({ name: '', price: 0 });
  const [newExtra, setNewExtra] = useState({ name: '', price: 0 });
  const [uploading, setUploading] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [catsRes, prodsRes] = await Promise.all([
        categoriesAPI.list(),
        productsAPI.list()
      ]);
      setCategories(catsRes.data);
      setProducts(prodsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleImportVendus = async () => {
    setImporting(true);
    try {
      const r = await productsAPI.importVendus();
      const d = r.data;
      toast.success(`Vendus: ${d.products_created} produtos novos, ${d.products_updated} atualizados, ${d.categories_created} categorias`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar do Vendus');
    } finally {
      setImporting(false);
    }
  };

  const [importingApp, setImportingApp] = useState(false);
  const handleImportAppProducts = async () => {
    setImportingApp(true);
    try {
      const r = await productsAPI.importAppProducts();
      const d = r.data;
      toast.success(`Produtos App: ${d.imported} importados para "Venda Aplicações"`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar produtos App');
    } finally {
      setImportingApp(false);
    }
  };

  const [seedingIva, setSeedingIva] = useState(false);
  const handleSeedIva = async () => {
    if (!window.confirm('Acertar o IVA em falta? Bebidas → 23% (NOR), restante comida → 13% (INT). Só mexe nos produtos SEM IVA definido.')) return;
    setSeedingIva(true);
    try {
      const r = await productsAPI.seedIvaDefaults();
      const d = r.data;
      toast.success(`IVA acertado: ${d.int_13} a 13% + ${d.nor_23} a 23% (${d.updated} produtos)`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao acertar o IVA');
    } finally {
      setSeedingIva(false);
    }
  };

  // Category handlers
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, order: category.order, active: category.active, available_days: category.available_days || [] });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', order: categories.length, active: true, available_days: [] });
    }
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Nome da categoria é obrigatório');
      return;
    }

    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, categoryForm);
        toast.success('Categoria atualizada');
      } else {
        await categoriesAPI.create(categoryForm);
        toast.success('Categoria criada');
      }
      setCategoryModalOpen(false);
      loadData();
    } catch (err) {
      console.error('Error saving category:', err);
      toast.error('Erro ao guardar categoria');
    }
  };

  const confirmDeleteCategory = (category) => {
    setDeleteTarget(category);
    setDeleteType('category');
    setDeleteDialogOpen(true);
  };

  const handleDeleteCategory = async () => {
    try {
      await categoriesAPI.delete(deleteTarget.id);
      toast.success('Categoria eliminada');
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error('Erro ao eliminar categoria');
    }
  };

  // Product handlers
  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        category_id: product.category_id,
        base_price: product.base_price,
        image_url: product.image_url || '',
        variations: product.variations || [],
        extras: product.extras || [],
        complement_groups: product.complement_groups || [],
        preference_options: product.preference_options || null,
        available: product.available,
        featured: product.featured,
        rodizio_incluido: product.rodizio_incluido || 'nao',
        rodizio_only: product.rodizio_only || false,
        vendus_tax_id: product.vendus_tax_id || 'INT'
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        category_id: categories[0]?.id || '',
        base_price: 0,
        image_url: '',
        variations: [],
        extras: [],
        complement_groups: [],
        preference_options: null,
        available: true,
        featured: false,
        rodizio_incluido: 'nao',
        rodizio_only: false,
        vendus_tax_id: 'INT'
      });
    }
    setNewVariation({ name: '', price: 0 });
    setNewExtra({ name: '', price: 0 });
    setProductModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await productsAPI.uploadImage(file);
      setProductForm(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Imagem carregada');
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error('Erro ao carregar imagem');
    } finally {
      setUploading(false);
    }
  };

  const addVariation = () => {
    if (!newVariation.name.trim() || newVariation.price <= 0) {
      toast.error('Preencha o nome e preço da variação');
      return;
    }
    setProductForm(prev => ({
      ...prev,
      variations: [...prev.variations, { ...newVariation }]
    }));
    setNewVariation({ name: '', price: 0 });
  };

  const removeVariation = (index) => {
    setProductForm(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  const addExtra = () => {
    if (!newExtra.name.trim() || newExtra.price <= 0) {
      toast.error('Preencha o nome e preço do extra');
      return;
    }
    setProductForm(prev => ({
      ...prev,
      extras: [...prev.extras, { ...newExtra }]
    }));
    setNewExtra({ name: '', price: 0 });
  };

  const removeExtra = (index) => {
    setProductForm(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  const handleSaveProduct = async () => {
    if (!productForm.name.trim()) {
      toast.error('Nome do produto é obrigatório');
      return;
    }
    if (!productForm.category_id) {
      toast.error('Selecione uma categoria');
      return;
    }

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, productForm);
        toast.success('Produto atualizado');
      } else {
        await productsAPI.create(productForm);
        toast.success('Produto criado');
      }
      setProductModalOpen(false);
      loadData();
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error('Erro ao guardar produto');
    }
  };

  const confirmDeleteProduct = (product) => {
    setDeleteTarget(product);
    setDeleteType('product');
    setDeleteDialogOpen(true);
  };

  const handleDeleteProduct = async () => {
    try {
      await productsAPI.delete(deleteTarget.id);
      toast.success('Produto eliminado');
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      console.error('Error deleting product:', err);
      toast.error('Erro ao eliminar produto');
    }
  };

  const toggleProductAvailability = async (product) => {
    try {
      await productsAPI.update(product.id, { available: !product.available });
      toast.success(product.available ? 'Produto indisponível' : 'Produto disponível');
      loadData();
    } catch (err) {
      console.error('Error toggling availability:', err);
      toast.error('Erro ao alterar disponibilidade');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Sem categoria';
  };

  // Reordenar produtos dentro da categoria filtrada (só disponível com filtro ativo)
  const moveProduct = async (list, idx, dir) => {
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const items = reordered.map((p, i) => ({ id: p.id, order: i }));
    try {
      const res = await productsAPI.reorder(items);
      setProducts(res.data);
      toast.success('Ordem atualizada');
    } catch {
      toast.error('Erro ao reordenar');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <AdminLayout title="Menu">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const filteredProducts = categoryFilter === 'all'
    ? products
    : products.filter(p => p.category_id === categoryFilter);
  const reorderEnabled = categoryFilter !== 'all';

  return (
    <AdminLayout title="Menu">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="products">Produtos</TabsTrigger>
          <TabsTrigger value="categories">Categorias</TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <div className="flex items-center gap-3">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48" data-testid="product-category-filter">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {[...categories].sort((a, b) => a.order - b.order).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">{filteredProducts.length} produtos</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleSeedIva} disabled={seedingIva}>
                {seedingIva ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
                Acertar IVA em falta
              </Button>
              <Button variant="outline" onClick={handleImportVendus} disabled={importing}>
                {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Importar do Vendus
              </Button>
              <Button variant="outline" onClick={handleImportAppProducts} disabled={importingApp}>
                {importingApp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Importar produtos App (delivery)
              </Button>
              <Button onClick={() => openProductModal()} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                Novo Produto
              </Button>
            </div>
          </div>

          {reorderEnabled ? (
            <p className="text-xs text-muted-foreground mb-3">
              Usa as setas ↑↓ em cada produto para definir a ordem em que aparece no menu do cliente.
            </p>
          ) : (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              Para <strong>reordenar</strong> os produtos, escolhe primeiro uma <strong>categoria</strong> no filtro acima — aí aparecem setas ↑↓ em cada produto.
            </p>
          )}

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredProducts.map((product, idx) => (
              <Card key={product.id} className="overflow-hidden" data-testid={`product-admin-${product.id}`}>
                <div className="relative h-40">
                  <img
                    src={getImageUrl(product.image_url) || 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400'}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {!product.available && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Badge variant="destructive">Indisponível</Badge>
                    </div>
                  )}
                  {product.featured && (
                    <Badge className="absolute top-2 left-2 featured-badge border-0">
                      <Star className="h-3 w-3 mr-1" />
                      Destaque
                    </Badge>
                  )}
                  {product.rodizio_only && (
                    <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground border-0">
                      🍕 Só rodízio
                    </Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold">{product.name}</h3>
                      <p className="text-sm text-muted-foreground">{getCategoryName(product.category_id)}</p>
                    </div>
                    <span className="font-bold">€ {product.base_price.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {product.description}
                  </p>
                  <div className="flex gap-2 items-center">
                    <Button variant="outline" size="sm" onClick={() => openProductModal(product)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleProductAvailability(product)}
                    >
                      {product.available ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => confirmDeleteProduct(product)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                    {reorderEnabled && (
                      <div className="ml-auto flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === 0}
                          onClick={() => moveProduct(filteredProducts, idx, -1)} title="Mover para cima">
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === filteredProducts.length - 1}
                          onClick={() => moveProduct(filteredProducts, idx, 1)} title="Mover para baixo">
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="flex justify-between items-center mb-6">
            <p className="text-muted-foreground">{categories.length} categorias</p>
            <Button onClick={() => openCategoryModal()} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Categoria
            </Button>
          </div>

          <div className="space-y-3">
            {[...categories].sort((a, b) => a.order - b.order).map((category, idx, sorted) => (
              <Card key={category.id} data-testid={`category-admin-${category.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Move Up/Down Buttons */}
                    <div className="flex flex-col gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={idx === 0}
                        onClick={async () => {
                          const prev = sorted[idx - 1];
                          const newItems = sorted.map((c, i) => {
                            if (c.id === category.id) return { id: c.id, order: prev.order };
                            if (c.id === prev.id) return { id: c.id, order: category.order };
                            return { id: c.id, order: c.order };
                          });
                          try {
                            const res = await categoriesAPI.reorder(newItems);
                            setCategories(res.data);
                            toast.success('Ordem atualizada');
                          } catch { toast.error('Erro ao reordenar'); }
                        }}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={idx === sorted.length - 1}
                        onClick={async () => {
                          const next = sorted[idx + 1];
                          const newItems = sorted.map((c) => {
                            if (c.id === category.id) return { id: c.id, order: next.order };
                            if (c.id === next.id) return { id: c.id, order: category.order };
                            return { id: c.id, order: c.order };
                          });
                          try {
                            const res = await categoriesAPI.reorder(newItems);
                            setCategories(res.data);
                            toast.success('Ordem atualizada');
                          } catch { toast.error('Erro ao reordenar'); }
                        }}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <h3 className="font-semibold">{category.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {products.filter(p => p.category_id === category.id).length} produtos • Posição {idx + 1}
                      </p>
                    </div>
                    {!category.active && (
                      <Badge variant="secondary">Inativa</Badge>
                    )}
                    {category.available_days?.length > 0 && (
                      <Badge variant="outline" className="font-normal">
                        {category.available_days
                          .slice()
                          .sort((a, b) => a - b)
                          .map(d => ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'][d])
                          .join(', ')}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openCategoryModal(category)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => confirmDeleteCategory(category)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Nome</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Pizzas"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-order">Ordem</Label>
              <Input
                id="cat-order"
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Dias disponíveis</Label>
              <div className="flex flex-wrap gap-1.5">
                {[['Seg', 0], ['Ter', 1], ['Qua', 2], ['Qui', 3], ['Sex', 4], ['Sáb', 5], ['Dom', 6]].map(([lbl, d]) => {
                  const on = categoryForm.available_days.includes(d);
                  return (
                    <button key={d} type="button"
                      onClick={() => setCategoryForm(prev => ({
                        ...prev,
                        available_days: on ? prev.available_days.filter(x => x !== d) : [...prev.available_days, d].sort((a, b) => a - b),
                      }))}
                      className={`h-9 w-12 rounded-lg border text-sm font-medium transition ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Nenhum selecionado = aparece todos os dias.</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="cat-active"
                checked={categoryForm.active}
                onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, active: checked }))}
              />
              <Label htmlFor="cat-active">Ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCategory}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={productModalOpen} onOpenChange={setProductModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Image */}
            <div className="space-y-2">
              <Label>Imagem</Label>
              <div className="flex items-center gap-4">
                {productForm.image_url ? (
                  <img
                    src={getImageUrl(productForm.image_url)}
                    alt="Preview"
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-24 h-24 bg-secondary rounded-lg flex items-center justify-center">
                    <ImagePlus className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="w-auto"
                  />
                  {uploading && <p className="text-sm text-muted-foreground mt-1">A carregar...</p>}
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="prod-name">Nome</Label>
                <Input
                  id="prod-name"
                  value={productForm.name}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Margherita"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prod-category">Categoria</Label>
                <Select 
                  value={productForm.category_id}
                  onValueChange={(value) => setProductForm(prev => ({ ...prev, category_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prod-desc">Descrição</Label>
              <Textarea
                id="prod-desc"
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição do produto"
                rows={3}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="prod-price">Preço Base (€)</Label>
                <Input
                  id="prod-price"
                  type="number"
                  step="0.01"
                  value={productForm.base_price}
                  onChange={(e) => setProductForm(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-4 pt-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="prod-available"
                    checked={productForm.available}
                    onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, available: checked }))}
                  />
                  <Label htmlFor="prod-available">Disponível</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="prod-featured"
                    checked={productForm.featured}
                    onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, featured: checked }))}
                  />
                  <Label htmlFor="prod-featured">Destaque</Label>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="prod-rodizio">Incluído no rodízio</Label>
                  <Select value={productForm.rodizio_incluido}
                    onValueChange={(v) => setProductForm(prev => ({ ...prev, rodizio_incluido: v }))}>
                    <SelectTrigger id="prod-rodizio"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nao">Não</SelectItem>
                      <SelectItem value="ambos">Simples e Completo</SelectItem>
                      <SelectItem value="completo">Só Completo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="prod-iva">IVA (fatura Vendus)</Label>
                  <Select value={productForm.vendus_tax_id || 'INT'}
                    onValueChange={(v) => setProductForm(prev => ({ ...prev, vendus_tax_id: v }))}>
                    <SelectTrigger id="prod-iva"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INT">13% — Intermédia (comida, águas)</SelectItem>
                      <SelectItem value="NOR">23% — Normal (refrigerantes, sumos)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-start gap-2">
                  <Switch
                    id="prod-rodizio-only"
                    checked={productForm.rodizio_only}
                    onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, rodizio_only: checked }))}
                  />
                  <div>
                    <Label htmlFor="prod-rodizio-only">Só disponível no rodízio</Label>
                    <p className="text-xs text-muted-foreground">Esconde do menu normal; só aparece a quem está em rodízio.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Variations */}
            <div className="space-y-3">
              <Label>Variações (Tamanhos)</Label>
              {productForm.variations.map((variation, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg">
                  <span className="flex-1">{variation.name}</span>
                  <span className="font-semibold">€ {variation.price.toFixed(2)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeVariation(idx)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Nome (ex: Média)"
                  value={newVariation.name}
                  onChange={(e) => setNewVariation(prev => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Preço"
                  value={newVariation.price || ''}
                  onChange={(e) => setNewVariation(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="w-28"
                />
                <Button variant="outline" onClick={addVariation}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Extras */}
            <div className="space-y-3">
              <Label>Extras</Label>
              {productForm.extras.map((extra, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg">
                  <span className="flex-1">{extra.name}</span>
                  <span className="font-semibold">+ € {extra.price.toFixed(2)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeExtra(idx)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Nome (ex: Borda recheada)"
                  value={newExtra.name}
                  onChange={(e) => setNewExtra(prev => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Preço"
                  value={newExtra.price || ''}
                  onChange={(e) => setNewExtra(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="w-28"
                />
                <Button variant="outline" onClick={addExtra}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Complement Groups */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Grupos de Complementos</Label>
                <Button variant="outline" size="sm" onClick={() => {
                  setProductForm(prev => ({
                    ...prev,
                    complement_groups: [...prev.complement_groups, { name: '', min_selections: 0, max_selections: 4, items: [] }]
                  }));
                }}>
                  <Plus className="h-4 w-4 mr-1" /> Novo Grupo
                </Button>
              </div>
              {productForm.complement_groups.map((group, gIdx) => (
                <div key={gIdx} className="border rounded-lg p-4 space-y-3 bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Nome do grupo (ex: Molhos)"
                      value={group.name}
                      onChange={(e) => {
                        const updated = [...productForm.complement_groups];
                        updated[gIdx] = { ...updated[gIdx], name: e.target.value };
                        setProductForm(prev => ({ ...prev, complement_groups: updated }));
                      }}
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" onClick={() => {
                      setProductForm(prev => ({
                        ...prev,
                        complement_groups: prev.complement_groups.filter((_, i) => i !== gIdx)
                      }));
                    }}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Mínimo</Label>
                      <Input type="number" min="0" value={group.min_selections}
                        onChange={(e) => {
                          const updated = [...productForm.complement_groups];
                          updated[gIdx] = { ...updated[gIdx], min_selections: parseInt(e.target.value) || 0 };
                          setProductForm(prev => ({ ...prev, complement_groups: updated }));
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Máximo</Label>
                      <Input type="number" min="1" value={group.max_selections}
                        onChange={(e) => {
                          const updated = [...productForm.complement_groups];
                          updated[gIdx] = { ...updated[gIdx], max_selections: parseInt(e.target.value) || 1 };
                          setProductForm(prev => ({ ...prev, complement_groups: updated }));
                        }}
                      />
                    </div>
                  </div>
                  {/* Group Items */}
                  <div className="space-y-2">
                    {group.items.map((item, iIdx) => (
                      <div key={iIdx} className="flex items-center gap-2 p-2 bg-background rounded">
                        <span className="flex-1 text-sm">{item.name}</span>
                        {item.price > 0 && <span className="text-sm font-semibold">+ € {item.price.toFixed(2)}</span>}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                          const updated = [...productForm.complement_groups];
                          updated[gIdx] = { ...updated[gIdx], items: updated[gIdx].items.filter((_, i) => i !== iIdx) };
                          setProductForm(prev => ({ ...prev, complement_groups: updated }));
                        }}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input placeholder="Nome do complemento" id={`comp-name-${gIdx}`} className="flex-1" />
                      <Input type="number" step="0.01" placeholder="Preço" id={`comp-price-${gIdx}`} className="w-24" />
                      <Button variant="outline" size="sm" onClick={() => {
                        const nameEl = document.getElementById(`comp-name-${gIdx}`);
                        const priceEl = document.getElementById(`comp-price-${gIdx}`);
                        const name = nameEl?.value?.trim();
                        const price = parseFloat(priceEl?.value) || 0;
                        if (!name) { toast.error('Nome obrigatório'); return; }
                        const updated = [...productForm.complement_groups];
                        updated[gIdx] = { ...updated[gIdx], items: [...updated[gIdx].items, { name, price }] };
                        setProductForm(prev => ({ ...prev, complement_groups: updated }));
                        if (nameEl) nameEl.value = '';
                        if (priceEl) priceEl.value = '';
                      }}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Preference Options (for drinks) */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="pref-enabled"
                  checked={!!productForm.preference_options?.enabled}
                  onCheckedChange={(checked) => {
                    setProductForm(prev => ({
                      ...prev,
                      preference_options: checked
                        ? { enabled: true, label: 'Preferências', required: true,
                            min_selections: prev.preference_options?.min_selections ?? 0,
                            max_selections: prev.preference_options?.max_selections ?? 1,
                            options: prev.preference_options?.options || [] }
                        : null
                    }));
                  }}
                />
                <Label htmlFor="pref-enabled" className="text-base font-semibold">Opções de Preferência</Label>
              </div>
              {productForm.preference_options?.enabled && (
                <div className="border rounded-lg p-4 space-y-3 bg-secondary/30">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Título</Label>
                      <Input value={productForm.preference_options.label || ''}
                        onChange={(e) => setProductForm(prev => ({
                          ...prev, preference_options: { ...prev.preference_options, label: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="flex items-end gap-2 pb-1">
                      <Switch
                        checked={productForm.preference_options.required}
                        onCheckedChange={(checked) => setProductForm(prev => ({
                          ...prev, preference_options: { ...prev.preference_options, required: checked }
                        }))}
                      />
                      <Label className="text-xs">Obrigatório</Label>
                    </div>
                  </div>

                  {/* Quantas o cliente pode escolher. Máximo 1 = como sempre
                      foi (escolha única). Máximo > 1 deixa marcar várias (ex.:
                      Com Gelo + Limão), sem precisar da opção combinada. */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Mínimo a escolher</Label>
                      <Input type="number" min="0"
                        value={productForm.preference_options.min_selections ?? 0}
                        onChange={(e) => setProductForm(prev => ({
                          ...prev, preference_options: {
                            ...prev.preference_options,
                            min_selections: parseInt(e.target.value, 10) || 0,
                          }
                        }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Máximo a escolher</Label>
                      <Input type="number" min="1"
                        value={productForm.preference_options.max_selections ?? 1}
                        onChange={(e) => setProductForm(prev => ({
                          ...prev, preference_options: {
                            ...prev.preference_options,
                            max_selections: Math.max(1, parseInt(e.target.value, 10) || 1),
                          }
                        }))}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Máximo 1 = o cliente escolhe só uma (como até agora). Acima de 1,
                    pode marcar várias.
                  </p>
                  <div className="space-y-2">
                    {(productForm.preference_options.options || []).map((opt, oIdx) => (
                      <div key={oIdx} className="flex items-center gap-2 p-2 bg-background rounded">
                        <span className="flex-1 text-sm">{opt}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                          setProductForm(prev => ({
                            ...prev, preference_options: {
                              ...prev.preference_options,
                              options: prev.preference_options.options.filter((_, i) => i !== oIdx)
                            }
                          }));
                        }}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input placeholder="Ex: Com gelo" id="pref-new-option" className="flex-1" />
                      <Button variant="outline" size="sm" onClick={() => {
                        const el = document.getElementById('pref-new-option');
                        const val = el?.value?.trim();
                        if (!val) return;
                        setProductForm(prev => ({
                          ...prev, preference_options: {
                            ...prev.preference_options,
                            options: [...(prev.preference_options.options || []), val]
                          }
                        }));
                        if (el) el.value = '';
                      }}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveProduct}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar eliminação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que deseja eliminar {deleteType === 'category' ? 'esta categoria' : 'este produto'}?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteType === 'category' ? handleDeleteCategory : handleDeleteProduct}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminMenu;
